<script setup lang="ts">
import {computed} from "vue"
import {Volume2, ChevronLeft} from "lucide-vue-next"
import {Badge} from "@/components/ui/badge"
import {ConnectionState} from 'livekit-client'
import GlassControl from "~/components/voice-ui/GlassControl.vue"
import AnimatedBreadcrumbPage from "~/components/voice-ui/Chat/AnimatedBreadcrumbPage.vue"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
} from "~/components/ui/breadcrumb";
import {usePlatform} from "~/composables/usePlatform"
import {useCallOverlay} from "~/composables/useCallOverlay"

const props = defineProps<{
  channelName: string
  groupName?: string;
  roomState?: ConnectionState
  debugPanelOpen: boolean
}>()

// Going back doesn't disconnect the call — it minimizes the global Drawer (`MobileCallOverlay.vue`)
// to the floating bubble, same "minimized, still connected" pattern as desktop's `NavUser.vue`
// "return to call" shortcut, just reached from the other direction here. No navigation involved:
// the call view is a route-independent overlay, not tied to `/group/[id]`.
const {isMobile} = usePlatform()
const {minimizeCall} = useCallOverlay()

const statusText = computed(() => {
  switch (props.roomState) {
    case ConnectionState.Connected:
      return 'Подключено'
    case ConnectionState.Connecting:
      return 'Подключение...'
    case ConnectionState.Disconnected:
      return 'Отключено'
    case ConnectionState.Reconnecting:
      return 'Переподключение...'
    default:
      return 'Отключено'
  }
})

const statusDotClass = computed(() => {
  switch (props.roomState) {
    case ConnectionState.Connected:
      return 'bg-green-500'
    case ConnectionState.Connecting:
    case ConnectionState.Reconnecting:
      return 'bg-orange-500 animate-pulse'
    default:
      return 'bg-destructive'
  }
})

defineEmits<{
  (e: 'toggleDebug'): void
}>()
</script>

<template>
  <!-- Mobile: a plain bar matching the text-channel and friend-chat headers (ChatHeader.vue,
       pages/m-friends/[id].vue) rather than the floating glass pill desktop uses. -->
  <header v-if="isMobile" class="relative z-10 flex shrink-0 items-center gap-2 border-b bg-background p-3">
    <button
        type="button"
        aria-label="Свернуть звонок"
        class="-ml-1 flex size-8 shrink-0 items-center justify-center transition-transform duration-150 active:scale-90"
        @click="minimizeCall"
    >
      <ChevronLeft class="size-5"/>
    </button>
    <Volume2 class="size-5 shrink-0 text-muted-foreground"/>
    <div class="flex min-w-0 flex-1 flex-col">
      <span class="truncate font-medium leading-tight">{{ channelName }}</span>
      <span class="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span class="size-1.5 shrink-0 rounded-full" :class="statusDotClass"/>
        {{ statusText }}
      </span>
    </div>
  </header>
  <header v-else class="relative z-10 flex shrink-0 items-center gap-2 p-4 pr-2">
    <GlassControl>
      <template #left>
        <Volume2 class="size-5 text-primary"/>
      </template>

      <template #center>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <AnimatedBreadcrumbPage :text="channelName" class="text-primary"/>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </template>

      <template #right>
        <Transition
            enter-active-class="transition-all duration-300 ease-in-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition-all duration-300 ease-in-out"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
            mode="out-in"
        >
          <Badge
              v-if="roomState === ConnectionState.Connecting || roomState === ConnectionState.Reconnecting"
              :key="'connecting'"
              variant="orange"
              class="py-1"
          >
            <Tooltip>
              <TooltipTrigger>
                <div class="flex items-center gap-1">
                  <div class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0s_infinite]"></div>
                  <div class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.2s_infinite]"></div>
                  <div class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.4s_infinite]"></div>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{{ statusText }}</p>
              </TooltipContent>
            </Tooltip>
          </Badge>

          <Badge
              v-else-if="roomState === ConnectionState.Connected"
              :key="'connected'"
              variant="success"
              class="py-1 transition-all duration-300 ease-in-out overflow-hidden"
          >

            <div class="flex items-center gap-1">
              <div class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0s_infinite]"></div>
              <div
                  class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.2s_infinite]"></div>
              <div
                  class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.4s_infinite]"></div>
            </div>
          </Badge>
          <Badge
              v-else
              :key="'disconnected'"
              variant="destructive"
              class="py-1 transition-all duration-300 ease-in-out overflow-hidden"
              :class="{
                    'w-auto px-2': true,
                    'w-6 h-6 !p-0 rounded-full': false
                  }"
          >

            <Tooltip>
              <TooltipTrigger class="flex items-center justify-center w-full h-full">
                <Transition
                    enter-active-class="transition-all duration-300 ease-in-out"
                    enter-from-class="opacity-0 scale-50"
                    enter-to-class="opacity-100 scale-100"
                    leave-active-class="transition-all duration-300 ease-in-out"
                    leave-from-class="opacity-100 scale-100"
                    leave-to-class="opacity-0 scale-50"
                    mode="out-in"
                >
                  <div class="flex items-center gap-1">
                    <div class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0s_infinite]"></div>
                    <div
                        class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.2s_infinite]"></div>
                    <div
                        class="w-1 h-1 bg-white dark:bg-primary rounded-full animate-[bounce_1s_ease-in-out_0.4s_infinite]"></div>
                  </div>

                </Transition>
              </TooltipTrigger>
              <TooltipContent>
                <p>{{ statusText }}</p>
              </TooltipContent>
            </Tooltip>
          </Badge>
        </Transition>
      </template>
    </GlassControl>
  </header>
</template>
